import React from 'react';
import { Box, Button } from '@mui/material';
import { useThemeColors } from '../../../utils/useThemeColors';

// Docked, text-label counterpart to SmallFloatingToolbarMenu. Same actions
// (minus the "Show/Hide Original Toolbar" meta-toggle), shown above the
// TopBarNavMenu when the user picks the Top Bar navigation style.
//
// Laid out as two explicit rows so toggle labels that change width
// (View/Hide Details, Show Front/Back, ...) never reflow buttons between rows:
//   row 1 = document actions, row 2 = view controls (flip + zoom).
const TopBarToolbarMenu = ({
  isViewDetailsVisible,
  toggleViewDetails,
  displayPrices,
  togglePrices,
  isLoading,
  handlePdfDownload,
  handleSendEmail,
  handleSaveQuote,
  flipped,
  handleFlipToggle,
  handleZoomIn,
  handleZoomOut,
  hideFlip = false, // products without an inside/outside view (pleated screens)
}) => {
  const tc = useThemeColors();

  const row1 = [
    { id: 'viewDetails', label: isViewDetailsVisible ? 'Hide Details' : 'View Details', onClick: toggleViewDetails, disabled: false },
    { id: 'prices', label: displayPrices ? 'Hide Prices' : 'Show Prices', onClick: togglePrices, disabled: !isViewDetailsVisible },
    { id: 'pdf', label: isLoading ? 'Generating…' : 'PDF', onClick: handlePdfDownload, disabled: !isViewDetailsVisible || isLoading },
    { id: 'email', label: 'Email', onClick: () => handleSendEmail(true), disabled: !isViewDetailsVisible },
    { id: 'save', label: 'Save', onClick: () => handleSaveQuote(false), disabled: false },
    { id: 'exit', label: 'Save & Exit', onClick: () => handleSaveQuote(true), disabled: false },
  ];

  const row2 = [
    { id: 'flip', label: flipped ? 'Show Front' : 'Show Back', onClick: handleFlipToggle, disabled: false },
    { id: 'zoomIn', label: '+ Zoom In', onClick: handleZoomIn, disabled: false },
    { id: 'zoomOut', label: '− Zoom Out', onClick: handleZoomOut, disabled: false },
  ].filter(item => !(hideFlip && item.id === 'flip'));

  const renderButton = (item) => (
    <Button
      key={item.id}
      className="toolbar-button"
      onClick={item.onClick}
      disabled={item.disabled}
      sx={{
        fontSize: '12px',
        textTransform: 'none',
        minHeight: '22px',
        lineHeight: 1.1,
        py: 0,
        borderRadius: '4px',
        px: 1.2,
        backgroundColor: item.disabled ? tc.buttonBgDisabled : tc.headerBg,
        color: item.disabled ? tc.textDisabled : tc.text,
        opacity: item.disabled ? 0.5 : 1,
        '&:hover': { backgroundColor: item.disabled ? tc.buttonHoverDisabled : tc.headerHover },
      }}
    >
      {item.label}
    </Button>
  );

  const rowSx = { display: 'flex', flexWrap: 'wrap', gap: '4px' };

  return (
    <Box
      sx={{
        backgroundColor: tc.panelBg,
        border: `1px solid ${tc.panelBorder}`,
        borderRadius: '8px',
        boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
        p: '4px',
        mb: 0.5,
        display: 'flex',
        flexDirection: 'column',
        gap: '2px',
      }}
    >
      <Box sx={rowSx}>{row1.map(renderButton)}</Box>
      <Box sx={rowSx}>{row2.map(renderButton)}</Box>
    </Box>
  );
};

export default TopBarToolbarMenu;
